import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { User, LoginRequest, AuthResponse } from '../types'
import { useAuthStore } from '../stores/authStore'
import { useAutoLogout } from '../hooks/useAutoLogout'
import AutoLogoutWarning from '../components/AutoLogoutWarning'

interface AuthContextType {
  user: User | null
  login: (credentials: LoginRequest) => Promise<AuthResponse>
  logout: () => void
  loading: boolean
  error: string | null
  isAuthenticated: boolean
  clearError: () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: React.ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const authStore = useAuthStore()
  const hasVerified = useRef(false)
  const [showWarning, setShowWarning] = useState(false)
  const [warningSeconds, setWarningSeconds] = useState(60)

  useEffect(() => {
    // Wait for store hydration before verifying token
    // This prevents race conditions where verifyToken runs before persisted state is restored
    if (!hasVerified.current && authStore._hasHydrated) {
      hasVerified.current = true
      authStore.verifyToken()
    }
  }, [authStore._hasHydrated]) // Run when hydration completes

  useEffect(() => {
    // Fallback: if hydration doesn't complete within 1 second, verify anyway
    // This ensures the app doesn't hang if there's an issue with the persist middleware
    const fallbackTimer = setTimeout(() => {
      if (!hasVerified.current) {
        console.warn(
          'Auth store hydration timeout - verifying token as fallback',
        )
        hasVerified.current = true
        authStore.verifyToken()
      }
    }, 1000)

    return () => clearTimeout(fallbackTimer)
  }, [])

  // Set up auto-logout with warning
  const { resetTimer } = useAutoLogout({
    logout: authStore.logout,
    isAuthenticated: authStore.isAuthenticated,
    onWarning: (remainingSeconds) => {
      setWarningSeconds(remainingSeconds)
      setShowWarning(true)
    },
    warningTime: import.meta.env.VITE_AUTO_LOGOUT_TIMEOUT
      ? parseInt(import.meta.env.VITE_AUTO_LOGOUT_TIMEOUT)
      : 60 * 1000,
  })

  const handleStayLoggedIn = () => {
    setShowWarning(false)
    resetTimer()
  }

  const handleLogoutFromWarning = () => {
    setShowWarning(false)
    authStore.logout()
  }

  const value: AuthContextType = {
    user: authStore.user,
    login: authStore.login,
    logout: authStore.logout,
    loading: authStore.loading,
    error: authStore.error,
    isAuthenticated: authStore.isAuthenticated,
    clearError: authStore.clearError,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
      <AutoLogoutWarning
        isOpen={showWarning}
        remainingSeconds={warningSeconds}
        onStayLoggedIn={handleStayLoggedIn}
        onLogout={handleLogoutFromWarning}
      />
    </AuthContext.Provider>
  )
}
